import { getParentOrigin } from "../iframe/parentOrigin";
const isExternalUrl = (url) => /^https?:\/\//i.test(url) || url.startsWith("//");
// The app "home" screen is a "go back to the shop" destination (the default
// target of empty-state "Back to Shop" buttons). It has no standalone web
// surface — it's typically a `phoenix-multi-page` the single-screen web route
// can't render — and on web the "shop" IS the merchant storefront. So these
// route to the storefront, not a Tapcart screen inside the embed.
const SHOP_HOME_ROUTES = new Set(["home", "shop"]);
/**
 * Map a destination to one of: a same-origin web path, an external URL, or
 * the shop-home signal (navigate the merchant storefront). `{}` = unroutable.
 *
 * Exported for tests (pure, no DOM) — the URL parsing is where the edge
 * cases live (query preservation, nested paths, route vs id, external, home).
 */
export const resolveTarget = (appId, dest) => {
    if (!dest)
        return {};
    // Direct page / screen / layout id — navigate straight to it.
    if (dest.id &&
        (dest.type === "page" || dest.type === "screen" || dest.type === "layout")) {
        return {
            path: `/${encodeURIComponent(appId)}/web/${encodeURIComponent(dest.id)}`,
        };
    }
    // Resolve a route name (+ optional query) from the legacy destination
    // shapes:
    //   { type: "route", id: "wishlist-create" }   (route by id; id == a page's
    //                                                screenType)
    //   { url: "/wishlist-detail?wishlistId=X" }    (internal route url)
    //   { route: "wishlist-create" }                (route field)
    let routeName;
    let query = "";
    if (dest.type === "route" && dest.id) {
        routeName = dest.id;
    }
    else {
        const url = dest.url || (dest.route ? `/${dest.route}` : "");
        if (!url)
            return {};
        if (isExternalUrl(url)) {
            // Normalize protocol-relative (`//host`) to https so window.open gets an
            // explicit absolute URL rather than inheriting the iframe's protocol.
            return { external: url.startsWith("//") ? `https:${url}` : url };
        }
        const clean = url.replace(/^\/+/, "");
        const [pathPart, q] = clean.split("?");
        routeName = pathPart.split("/")[0];
        query = q || "";
    }
    if (!routeName)
        return {};
    if (SHOP_HOME_ROUTES.has(routeName))
        return { shopHome: true };
    // The SSR web route accepts a pageId OR a screenType segment.
    return {
        path: `/${encodeURIComponent(appId)}/web/${encodeURIComponent(routeName)}${query ? `?${query}` : ""}`,
    };
};
/**
 * Navigate the TOP window to the merchant storefront home — the right target
 * for a "Back to Shop" tap on the embedded surface.
 *
 * We need an ABSOLUTE storefront URL: a root-relative `top.location = "/"`
 * resolves against THIS (iframe) script's base origin per spec, which would
 * send the whole tab to the Tapcart origin instead of the storefront.
 *
 * Sources, best first:
 *   1. the origin captured from the host-bridge's postMessages, persisted in
 *      sessionStorage so it survives screen/open's full-page iframe reloads
 *      (exact, and independent of referrer policy);
 *   2. `document.referrer` (the embedding page — Shopify's default referrer
 *      policy sends at least the origin);
 *   3. `config.shopDomain` if it's ever wired (a `.myshopify` host, which may
 *      302 to the primary domain — last resort).
 *
 * Any candidate equal to our OWN origin is rejected: "Back to Shop" goes to
 * the merchant storefront, never the Tapcart/iframe origin. `document.referrer`
 * resolves to our own origin after a screen/open self-navigation (the previous
 * iframe page), or when the bundle is loaded top-level for testing — and
 * top-navigating there would wrongly send the storefront tab to Tapcart.
 *
 * Top navigation is permitted because this runs from a user click. If no
 * non-self origin is available (not embedded), degrade to navigating our own
 * frame rather than hijacking the tab to a wrong origin.
 */
const navigateToShopHome = (config) => {
    var _a;
    const ownOrigin = typeof window !== "undefined" && window.location
        ? window.location.origin
        : "";
    let referrerOrigin = "";
    if (typeof document !== "undefined" && document.referrer) {
        try {
            referrerOrigin = new URL(document.referrer).origin;
        }
        catch (_b) {
            // unparseable referrer — leave empty
        }
    }
    const shopDomainOrigin = config.shopDomain
        ? `https://${config.shopDomain.replace(/^https?:\/\//, "")}`
        : "";
    const storefrontOrigin = [
        (_a = getParentOrigin()) !== null && _a !== void 0 ? _a : "",
        referrerOrigin,
        shopDomainOrigin,
    ].find((origin) => origin && origin !== ownOrigin);
    if (storefrontOrigin) {
        try {
            ;
            (window.top || window).location.href = `${storefrontOrigin}/`;
            return;
        }
        catch (_c) {
            // top navigation blocked — fall through to same-frame nav
        }
    }
    // No real storefront origin (e.g. loaded top-level with no embedding
    // storefront) — there's no shop to return to, so stay in our own frame
    // rather than send the tab somewhere wrong.
    window.location.href = "/";
};
export const screenOpen = (data, handlers, ctx) => {
    var _a, _b, _c, _d, _e;
    if (typeof window === "undefined") {
        (_a = handlers.onSuccess) === null || _a === void 0 ? void 0 : _a.call(handlers, {});
        return;
    }
    const dest = (data !== null && data !== void 0 ? data : {}).destination;
    const { path, external, shopHome } = resolveTarget(ctx.config.appId, dest !== null && dest !== void 0 ? dest : {});
    if (shopHome) {
        navigateToShopHome(ctx.config);
        (_b = handlers.onSuccess) === null || _b === void 0 ? void 0 : _b.call(handlers, {});
        return;
    }
    if (external) {
        // External links leave the Tapcart surface — open in a new tab rather
        // than replacing the iframe (the iframe can't navigate the parent
        // cross-origin anyway). noopener+noreferrer so the opened page can't
        // reach back via window.opener and gets no referrer.
        window.open(external, "_blank", "noopener,noreferrer");
        (_c = handlers.onSuccess) === null || _c === void 0 ? void 0 : _c.call(handlers, {});
        return;
    }
    if (!path) {
        console.warn("[storefront-bridge] screen/open: unrecognized destination", dest);
        (_d = handlers.onError) === null || _d === void 0 ? void 0 : _d.call(handlers, new Error("screen/open: unrecognized destination"));
        return;
    }
    // Same-origin nav within the iframe (the iframe IS the consumer-web host).
    window.location.assign(path);
    (_e = handlers.onSuccess) === null || _e === void 0 ? void 0 : _e.call(handlers, {});
};
